// NotificationBell.tsx - مصحح لدعم تنبيهات المنتجات المركبة واستخدام low_stock_threshold من الإعدادات

import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from './api';
import { useNavigate } from 'react-router-dom';
import { API_URL, getAuthHeaders } from './config';

interface LowStockProduct {
  id: number;
  name_ar: string;
  name_en: string;
  stock_quantity: number;
  threshold?: number;
  min_stock_level?: number;
}

interface LowStockMaterial {
  id: number;
  name: string;
  current_stock: number;
  min_stock_level: number;
  unit: string;
}

interface ReservationAlert {
  id: number;
  table_number: string;
  reserved_by: string;
  reservation_time: string;
  status: 'near' | 'overdue';
}

interface ExpiryAlert {
  material_id: number;
  material_name: string;
  batch_number: string;
  expiry_date: string;
  quantity: number;
  status: 'expired' | 'expiring_soon';
  days_left?: number;
  days_overdue?: number;
}

interface RecipeStatusAlert {
  product_id: number;
  product_name: string;
  missing_materials: Array<{
    material_name: string;
    required: number;
    available: number;
    shortage: number;
    unit: string;
  }>;
}

interface Settings {
  low_stock_threshold?: number;
}

function NotificationBell() {
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState<LowStockProduct[]>([]);
  const [lowStockMaterials, setLowStockMaterials] = useState<LowStockMaterial[]>([]);
  const [reservationAlerts, setReservationAlerts] = useState<ReservationAlert[]>([]);
  const [expiryAlerts, setExpiryAlerts] = useState<ExpiryAlert[]>([]);
  const [disabledRecipes, setDisabledRecipes] = useState<RecipeStatusAlert[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings>({
    low_stock_threshold: 2  // قيمة افتراضية
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // دوال التحقق من الحجوزات
  const isReservationNear = useCallback((reservationTime: string): boolean => {
    try {
      const now = new Date();
      const reservationDate = new Date(reservationTime);
      const diffHours = (reservationDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      return diffHours <= 2 && diffHours > 0;
    } catch {
      return false;
    }
  }, []);

  const isReservationOverdue = useCallback((reservationTime: string): boolean => {
    try {
      const now = new Date();
      const reservationDate = new Date(reservationTime);
      return reservationDate < now;
    } catch {
      return false;
    }
  }, []);

  // جلب الحجوزات من السيرفر
  const fetchReservations = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables`, {
        headers: getAuthHeaders(),
        timeout: 5000
      });

      const tables = Array.isArray(response.data) ? response.data : [];
      const alerts: ReservationAlert[] = [];

      tables.forEach((table: any) => {
        if (table.status === 'reserved' && table.reservation_time) {
          if (isReservationOverdue(table.reservation_time)) {
            alerts.push({
              id: table.id,
              table_number: table.table_number,
              reserved_by: table.reserved_by || 'غير معروف',
              reservation_time: table.reservation_time,
              status: 'overdue'
            });
          } else if (isReservationNear(table.reservation_time)) {
            alerts.push({
              id: table.id,
              table_number: table.table_number,
              reserved_by: table.reserved_by || 'غير معروف',
              reservation_time: table.reservation_time,
              status: 'near'
            });
          }
        }
      });

      setReservationAlerts(alerts);
    } catch (error) {
      console.error('خطأ في جلب الحجوزات:', error);
    }
  }, [isReservationNear, isReservationOverdue]);

  // جلب الإعدادات من قاعدة البيانات عبر API
  const loadSettings = useCallback(async () => {
    try {
      // ✅ محاولة جلب الإعدادات من قاعدة البيانات
      const response = await api.get(`${API_URL}/api/settings`);
      if (Array.isArray(response.data)) {
        const thresholdSetting = response.data.find((s: any) => s.key === 'low_stock_threshold');
        if (thresholdSetting && thresholdSetting.value) {
          setSettings({
            low_stock_threshold: parseInt(thresholdSetting.value) || 5
          });
        }
      }
    } catch (error) {
      console.error('خطأ في تحميل الإعدادات من السيرفر:', error);
      
      // ✅ fallback للإعدادات المحلية
      try {
        const saved = localStorage.getItem('printer_settings');
        if (saved) {
          const parsed = JSON.parse(saved);
          setSettings({
            low_stock_threshold: parsed.low_stock_threshold || 5
          });
        }
      } catch (localError) {
        console.error('خطأ في تحميل الإعدادات المحلية:', localError);
      }
    }
  }, []);

  // جلب المنتجات منخفضة المخزون والنافدة
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`${API_URL}/api/products`, {
        headers: getAuthHeaders(),
        timeout: 5000
      });
      
      const allProducts = Array.isArray(response.data) ? response.data : [];
      
      const threshold = settings.low_stock_threshold || 2;
      
      // ✅ فلترة المنتجات العادية فقط (المركبة مش بنحسبها هنا)
      const normalProducts = allProducts.filter((p: any) => p.product_type !== 'recipe');
      
      // فلترة المنتجات منخفضة المخزون (أقل من الحد المحدد)
      const lowStock = normalProducts.filter((p: any) => 
        p.stock_quantity <= (p.min_stock_level || threshold) && p.stock_quantity > 0
      );
      
      // فلترة المنتجات النافدة
      const outOfStock = normalProducts.filter((p: any) => p.stock_quantity === 0);
      
      setLowStockProducts(lowStock);
      setOutOfStockProducts(outOfStock);
      
    } catch (error) {
      console.error('خطأ في جلب المنتجات:', error);
      setError('فشل في تحميل الإشعارات');
    } finally {
      setLoading(false);
    }
  }, [settings.low_stock_threshold]);

  // جلب المواد الخام منخفضة المخزون
  const fetchLowStockMaterials = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/api/raw-materials/low-stock/summary`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.materials) {
        setLowStockMaterials(response.data.materials);
      }
    } catch (error) {
      console.error('خطأ في جلب المواد منخفضة المخزون:', error);
    }
  }, []);

  // جلب تنبيهات الصلاحية
  const fetchExpiryAlerts = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/api/raw-materials/expiry/alerts?days_threshold=7`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.alerts) {
        setExpiryAlerts([
          ...(response.data.alerts.expiring_soon?.batches || []),
          ...(response.data.alerts.expired?.batches || [])
        ]);
      }
    } catch (error) {
      console.error('خطأ في جلب تنبيهات الصلاحية:', error);
    }
  }, []);

  // التحقق من المنتجات المركبة المعطلة
  const fetchDisabledRecipes = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/api/products?product_type=recipe&include_inactive=true`, {
        headers: getAuthHeaders()
      });
      
      const products = Array.isArray(response.data) ? response.data : [];
      const disabledProducts: RecipeStatusAlert[] = [];
      
      for (const product of products) {
        // ✅ المنتج معطل إذا كان is_active = false
        if (!product.is_active) {
          // جلب تفاصيل المواد الناقصة
          try {
            const checkResponse = await api.get(
              `${API_URL}/api/recipes/product/${product.id}/check?quantity=1`,
              { headers: getAuthHeaders() }
            );
            
            if (checkResponse.data.missing_materials && checkResponse.data.missing_materials.length > 0) {
              disabledProducts.push({
                product_id: product.id,
                product_name: product.name_ar,
                missing_materials: checkResponse.data.missing_materials.map((m: any) => ({
                  material_name: m.material_name,
                  required: m.required,
                  available: m.available,
                  shortage: m.shortage,
                  unit: m.unit
                }))
              });
            } else {
              disabledProducts.push({
                product_id: product.id,
                product_name: product.name_ar,
                missing_materials: []
              });
            }
          } catch (error) {
            console.error(`خطأ في جلب تفاصيل المنتج ${product.id}:`, error);
            disabledProducts.push({
              product_id: product.id,
              product_name: product.name_ar,
              missing_materials: []
            });
          }
        }
      }
      
      setDisabledRecipes(disabledProducts);
      
    } catch (error) {
      console.error('خطأ في جلب المنتجات المعطلة:', error);
    }
  }, []);

  // إغلاق القائمة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && 
          buttonRef.current && 
          !dropdownRef.current.contains(event.target as Node) && 
          !buttonRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // منع التمرير الخلفي عند فتح القائمة
  useEffect(() => {
    if (showDropdown) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDropdown]);

  useEffect(() => {
    loadSettings();
    fetchProducts();
    fetchLowStockMaterials();
    fetchReservations();
    fetchExpiryAlerts();
    fetchDisabledRecipes();

    // تحديث كل 60 ثانية
    const interval = setInterval(() => {
      fetchProducts();
      fetchLowStockMaterials();
      fetchReservations();
      fetchExpiryAlerts();
      fetchDisabledRecipes();
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchProducts, fetchLowStockMaterials, fetchReservations, fetchExpiryAlerts, fetchDisabledRecipes, loadSettings]);

  // إجمالي التنبيهات
  const totalAlerts = lowStockProducts.length + outOfStockProducts.length + 
                      lowStockMaterials.length + reservationAlerts.length + 
                      expiryAlerts.length + disabledRecipes.length;

  const handleMarkAsRead = useCallback((productId: number, type: 'low' | 'out', e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === 'low') {
      setLowStockProducts(prev => prev.filter(p => p.id !== productId));
    } else {
      setOutOfStockProducts(prev => prev.filter(p => p.id !== productId));
    }
  }, []);

  const handleMarkMaterialAsRead = useCallback((materialId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setLowStockMaterials(prev => prev.filter(m => m.id !== materialId));
  }, []);

  const handleDismissReservation = useCallback((tableId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setReservationAlerts(prev => prev.filter(r => r.id !== tableId));
  }, []);

  const handleDismissExpiry = useCallback((index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpiryAlerts(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleDismissRecipe = useCallback((productId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDisabledRecipes(prev => prev.filter(r => r.product_id !== productId));
  }, []);

  const handleMarkAllAsRead = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setLowStockProducts([]);
    setOutOfStockProducts([]);
    setLowStockMaterials([]);
    setReservationAlerts([]);
    setExpiryAlerts([]);
    setDisabledRecipes([]);
  }, []);

  // دوال التنقل
  const goToRawMaterials = useCallback(() => {
    setShowDropdown(false);
    navigate('/raw-materials');
  }, [navigate]);

  const goToAdmin = useCallback(() => {
    setShowDropdown(false);
    navigate('/admin');
  }, [navigate]);

  const goToTables = useCallback(() => {
    setShowDropdown(false);
    navigate('/tables');
  }, [navigate]);

  const goToRecipes = useCallback(() => {
    setShowDropdown(false);
    navigate('/recipes');
  }, [navigate]);

  const toggleDropdown = useCallback(() => {
    setShowDropdown(prev => !prev);
  }, []);

  // دوال التنسيق
  const formatTime = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('ar-EG', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  }, []);

  const formatDate = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }, []);

  const formatQuantity = useCallback((quantity: number, unit: string) => {
    if (unit === 'kg' && quantity < 1) {
      return `${(quantity * 1000).toFixed(0)} جم`;
    }
    if (unit === 'liter' && quantity < 1) {
      return `${(quantity * 1000).toFixed(0)} مل`;
    }
    return `${quantity} ${unit}`;
  }, []);

  const bellStyle: React.CSSProperties = {
    position: 'relative',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    backgroundColor: showDropdown ? '#e0e0e0' : 'transparent',
    transition: 'background-color 0.2s, transform 0.2s',
    display: 'inline-block',
    transform: showDropdown ? 'scale(1.1)' : 'scale(1)'
  };

  const badgeStyle: React.CSSProperties = {
    position: 'absolute',
    top: '0',
    right: '0',
    backgroundColor: totalAlerts > 0 ? '#f44336' : '#4CAF50',
    color: 'white',
    borderRadius: '50%',
    padding: '2px 6px',
    fontSize: '10px',
    minWidth: '16px',
    textAlign: 'center',
    transform: 'translate(25%, -25%)',
    fontWeight: 'bold'
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '45px',
    left: '0',
    right: 'auto',
    width: '380px',
    maxHeight: '500px',
    overflowY: 'auto',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    zIndex: 10000,
    padding: '12px',
    border: '1px solid #e0e0e0',
    animation: 'slideDown 0.2s ease'
  };

  const productItemStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'background-color 0.2s',
    cursor: 'default'
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        ref={buttonRef}
        style={bellStyle}
        onClick={toggleDropdown}
        onMouseEnter={(e) => {
          if (!showDropdown) {
            e.currentTarget.style.backgroundColor = '#e0e0e0';
            e.currentTarget.style.transform = 'scale(1.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (!showDropdown) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.transform = 'scale(1)';
          }
        }}
        role="button"
        aria-label="الإشعارات"
        tabIndex={0}
      >
        <span style={{ fontSize: '22px' }}>🔔</span>
        {totalAlerts > 0 && (
          <span style={badgeStyle}>
            {totalAlerts > 99 ? '99+' : totalAlerts}
          </span>
        )}
      </div>

      {showDropdown && (
        <div ref={dropdownRef} style={dropdownStyle}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: '2px solid #4CAF50',
            marginBottom: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ fontSize: '20px' }}>🔔</span>
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>الإشعارات</h3>
            </div>
            {totalAlerts > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  padding: '4px 10px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  transition: 'background-color 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4CAF50'}
              >
                تحديد الكل كمقروء
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
              <div className="loading-spinner" style={{ width: '30px', height: '30px', margin: '0 auto 10px' }}></div>
              <p style={{ fontSize: '14px' }}>جاري التحميل...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#f44336' }}>
              <p>{error}</p>
            </div>
          ) : totalAlerts === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
              <p style={{ fontSize: '14px', fontWeight: 'bold' }}>لا توجد إشعارات</p>
              <p style={{ fontSize: '12px', marginTop: '5px' }}>كل المنتجات متوفرة والحجوزات سليمة</p>
            </div>
          ) : (
            <>
              {/* المنتجات المركبة المعطلة (الأهم) */}
              {disabledRecipes.length > 0 && (
                <>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    fontWeight: 'bold',
                    borderRadius: '20px',
                    margin: '5px 0',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span>🚫</span>
                    <span>منتجات مركبة معطلة ({disabledRecipes.length})</span>
                  </div>
                  {disabledRecipes.slice(0, 5).map((recipe) => (
                    <div
                      key={`dis-${recipe.product_id}`}
                      style={{
                        padding: '10px',
                        backgroundColor: '#f3e5f5',
                        borderRadius: '8px',
                        marginBottom: '5px',
                        border: '1px solid #9C27B0',
                        cursor: 'pointer'
                      }}
                      onClick={goToRecipes}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                        {recipe.product_name}
                      </div>
                      {recipe.missing_materials.length > 0 ? (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                          المواد الناقصة:
                          {recipe.missing_materials.slice(0, 2).map((m, idx) => (
                            <div key={idx} style={{ marginTop: '2px' }}>
                              • {m.material_name}: {formatQuantity(m.shortage, m.unit)}
                            </div>
                          ))}
                          {recipe.missing_materials.length > 2 && (
                            <div>+ {recipe.missing_materials.length - 2} مواد أخرى</div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                          معطل لعدم توفر المواد الخام
                        </div>
                      )}
                      <button
                        onClick={(e) => handleDismissRecipe(recipe.product_id, e)}
                        style={{
                          marginTop: '5px',
                          padding: '2px 8px',
                          backgroundColor: '#9C27B0',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        تم
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* تنبيهات انتهاء الصلاحية */}
              {expiryAlerts.length > 0 && (
                <>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    fontWeight: 'bold',
                    borderRadius: '20px',
                    margin: '5px 0',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span>⚠️</span>
                    <span>تنبيهات انتهاء الصلاحية ({expiryAlerts.length})</span>
                  </div>
                  {expiryAlerts.slice(0, 5).map((alert, index) => (
                    <div
                      key={`exp-${index}`}
                      style={{
                        padding: '10px',
                        backgroundColor: alert.status === 'expired' ? '#ffebee' : '#fff3e0',
                        borderRadius: '8px',
                        marginBottom: '5px',
                        border: alert.status === 'expired' ? '1px solid #f44336' : '1px solid #FF9800',
                        cursor: 'pointer'
                      }}
                      onClick={goToRawMaterials}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                        {alert.material_name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        شحنة: {alert.batch_number}<br />
                        الكمية: {formatQuantity(alert.quantity, '')}<br />
                        تاريخ الصلاحية: {formatDate(alert.expiry_date)}
                      </div>
                      {alert.status === 'expired' ? (
                        <div style={{ color: '#f44336', fontSize: '11px', fontWeight: 'bold', marginTop: '3px' }}>
                          ⚠️ منتهي منذ {alert.days_overdue} أيام
                        </div>
                      ) : (
                        <div style={{ color: '#FF9800', fontSize: '11px', fontWeight: 'bold', marginTop: '3px' }}>
                          ⏰ متبقي {alert.days_left} أيام
                        </div>
                      )}
                      <button
                        onClick={(e) => handleDismissExpiry(index, e)}
                        style={{
                          marginTop: '5px',
                          padding: '2px 8px',
                          backgroundColor: alert.status === 'expired' ? '#f44336' : '#FF9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        تم
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* تنبيهات المواد الخام منخفضة المخزون */}
              {lowStockMaterials.length > 0 && (
                <>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#FF9800',
                    color: 'white',
                    fontWeight: 'bold',
                    borderRadius: '20px',
                    margin: '5px 0',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span>🧪</span>
                    <span>مواد خام منخفضة المخزون ({lowStockMaterials.length})</span>
                  </div>
                  {lowStockMaterials.slice(0, 5).map(material => (
                    <div
                      key={`mat-${material.id}`}
                      style={{
                        padding: '10px',
                        backgroundColor: '#fff3e0',
                        borderRadius: '8px',
                        marginBottom: '5px',
                        border: '1px solid #FF9800',
                        cursor: 'pointer'
                      }}
                      onClick={goToRawMaterials}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                        {material.name}
                      </div>
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                        المتاح: {formatQuantity(material.current_stock, material.unit)} من أصل {formatQuantity(material.min_stock_level, material.unit)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#f44336', marginTop: '3px' }}>
                        العجز: {formatQuantity(material.min_stock_level - material.current_stock, material.unit)}
                      </div>
                      <button
                        onClick={(e) => handleMarkMaterialAsRead(material.id, e)}
                        style={{
                          marginTop: '5px',
                          padding: '2px 8px',
                          backgroundColor: '#FF9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        تم
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* تنبيهات الحجوزات */}
              {reservationAlerts.length > 0 && (
                <>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    fontWeight: 'bold',
                    borderRadius: '20px',
                    margin: '5px 0',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span>📅</span>
                    <span>تنبيهات الحجوزات ({reservationAlerts.length})</span>
                  </div>
                  {reservationAlerts.map(alert => (
                    <div
                      key={`res-${alert.id}`}
                      style={{
                        ...productItemStyle,
                        backgroundColor: alert.status === 'overdue' ? '#ffebee' : '#fff3e0',
                        borderRadius: '8px',
                        marginBottom: '5px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = alert.status === 'overdue' ? '#ffcdd2' : '#ffe0b2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = alert.status === 'overdue' ? '#ffebee' : '#fff3e0'}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {alert.status === 'overdue' ? '⚠️' : '⏰'} ترابيزة {alert.table_number}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <div>👤 {alert.reserved_by}</div>
                          <div>⏱️ {formatTime(alert.reservation_time)}</div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDismissReservation(alert.id, e)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: alert.status === 'overdue' ? '#f44336' : '#FF9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = alert.status === 'overdue' ? '#d32f2f' : '#F57C00'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = alert.status === 'overdue' ? '#f44336' : '#FF9800'}
                      >
                        تم
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* المنتجات العادية النافدة */}
              {outOfStockProducts.length > 0 && (
                <>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#ffebee',
                    color: '#f44336',
                    fontWeight: 'bold',
                    borderRadius: '20px',
                    margin: '5px 0',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span>⛔</span>
                    <span>منتجات نافدة ({outOfStockProducts.length})</span>
                  </div>
                  {outOfStockProducts.map(product => (
                    <div
                      key={`out-${product.id}`}
                      style={{
                        ...productItemStyle,
                        backgroundColor: '#fff5f5',
                        borderRadius: '8px',
                        marginBottom: '5px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ffe5e5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{product.name_ar}</div>
                        <div style={{ fontSize: '12px', color: '#f44336' }}>
                          الكمية: {product.stock_quantity}
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleMarkAsRead(product.id, 'out', e)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f44336'}
                      >
                        تم
                      </button>
                    </div>
                  ))}
                </>
              )}

              {/* المنتجات العادية منخفضة المخزون */}
              {lowStockProducts.length > 0 && (
                <>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    fontWeight: 'bold',
                    borderRadius: '20px',
                    margin: '10px 0 5px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span>⚠️</span>
                    <span>منتجات منخفضة المخزون ({lowStockProducts.length})</span>
                  </div>
                  {lowStockProducts.map(product => (
                    <div
                      key={`low-${product.id}`}
                      style={{
                        ...productItemStyle,
                        backgroundColor: '#fff8e7',
                        borderRadius: '8px',
                        marginBottom: '5px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ffefd5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff8e7'}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{product.name_ar}</div>
                        <div style={{ fontSize: '12px', color: '#856404' }}>
                          الكمية: {product.stock_quantity} (أقل من {product.min_stock_level || settings.low_stock_threshold})
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleMarkAsRead(product.id, 'low', e)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#FF9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F57C00'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF9800'}
                      >
                        تم
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          <div style={{
            padding: '10px',
            borderTop: '1px solid #e0e0e0',
            marginTop: '10px',
            textAlign: 'center'
          }}>
            <button
              onClick={goToAdmin}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                transition: 'background-color 0.3s, transform 0.2s',
                width: '100%',
                marginBottom: '5px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1976D2';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2196F3';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              عرض كل المنتجات
            </button>
            
            {lowStockMaterials.length > 0 && (
              <button
                onClick={goToRawMaterials}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#FF9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  transition: 'background-color 0.3s, transform 0.2s',
                  width: '100%',
                  marginBottom: '5px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F57C00';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FF9800';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                عرض المواد الخام
              </button>
            )}
            
            {disabledRecipes.length > 0 && (
              <button
                onClick={goToRecipes}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#9C27B0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  transition: 'background-color 0.3s, transform 0.2s',
                  width: '100%',
                  marginBottom: '5px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#7B1FA2';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#9C27B0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                عرض الوصفات
              </button>
            )}
            
            {reservationAlerts.length > 0 && (
              <button
                onClick={goToTables}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#FF9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  transition: 'background-color 0.3s, transform 0.2s',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F57C00';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FF9800';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                عرض الترابيزات
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;